type ParamValue = string | string[]
type ParamValues = Record<string, ParamValue>

type ParamsMessage =
  | {
      source: 'asset-params'
      type: 'ready'
      instanceId: string
    }
  | {
      source: 'asset-params'
      type: 'update'
      target: string
      values?: Record<string, unknown>
      revision?: number
    }

const windowsByInstance = new Map<string, Window>()
const paramsByInstance = new Map<string, ParamValues>()
const revisionByInstance = new Map<string, number>()
const readyWindowByInstance = new Map<string, Window>()

let listenerAttached = false

const normalizeValue = (value: unknown): ParamValue | null => {
  if (value === null || value === undefined) return null
  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && item !== undefined).map((item) => String(item))
  }
  return String(value)
}

const normalizeValues = (values?: Record<string, unknown> | null): ParamValues => {
  if (!values) return {}
  const result: ParamValues = {}
  Object.entries(values).forEach(([key, value]) => {
    const normalized = normalizeValue(value)
    if (normalized === null) return
    result[key] = normalized
  })
  return result
}

const notifyInstance = (instanceId: string) => {
  const targetWindow = windowsByInstance.get(instanceId)
  if (!targetWindow) return
  const readyWindow = readyWindowByInstance.get(instanceId)
  if (readyWindow !== targetWindow) return
  if (!revisionByInstance.has(instanceId)) return
  const values = paramsByInstance.get(instanceId) ?? {}
  const revision = revisionByInstance.get(instanceId) ?? 0
  targetWindow.postMessage(
    {
      source: 'asset-params',
      type: 'update',
      target: instanceId,
      values,
      revision,
    },
    '*'
  )
}

const setParamsForInstance = (
  instanceId: string,
  values?: Record<string, unknown> | null,
  options?: { revision?: number }
) => {
  paramsByInstance.set(instanceId, normalizeValues(values))
  const explicitRevision = options?.revision
  if (typeof explicitRevision === 'number' && Number.isFinite(explicitRevision) && explicitRevision >= 0) {
    revisionByInstance.set(instanceId, Math.trunc(explicitRevision))
  } else {
    revisionByInstance.set(instanceId, (revisionByInstance.get(instanceId) ?? 0) + 1)
  }
  notifyInstance(instanceId)
}

const registerInstance = (instanceId: string, targetWindow: Window) => {
  if (!instanceId) return
  const previous = windowsByInstance.get(instanceId)
  windowsByInstance.set(instanceId, targetWindow)
  if (previous !== targetWindow) {
    readyWindowByInstance.delete(instanceId)
  }
  notifyInstance(instanceId)
}

const unregisterInstance = (instanceId: string, targetWindow?: Window | null) => {
  if (!instanceId) return
  const current = windowsByInstance.get(instanceId)
  if (!current) return
  if (!targetWindow || targetWindow === current) {
    windowsByInstance.delete(instanceId)
    readyWindowByInstance.delete(instanceId)
  }
}

const markInstanceReady = (instanceId: string, sourceWindow: Window) => {
  if (!instanceId) return
  windowsByInstance.set(instanceId, sourceWindow)
  readyWindowByInstance.set(instanceId, sourceWindow)
  notifyInstance(instanceId)
}

const forwardReadyToParent = (instanceId: string, sourceWindow: Window) => {
  if (!window.parent || window.parent === window) return
  if (sourceWindow === window.parent) return
  window.parent.postMessage(
    {
      source: 'asset-params',
      type: 'ready',
      instanceId,
    },
    '*'
  )
}

const handleMessage = (event: MessageEvent) => {
  const data = event.data as ParamsMessage | undefined
  if (!data || data.source !== 'asset-params') return
  if (data.type === 'ready' && typeof data.instanceId === 'string') {
    const sourceWindow = event.source instanceof Window ? event.source : windowsByInstance.get(data.instanceId) ?? null
    if (sourceWindow) {
      markInstanceReady(data.instanceId, sourceWindow)
      forwardReadyToParent(data.instanceId, sourceWindow)
    }
    return
  }
  if (data.type === 'update' && typeof data.target === 'string') {
    setParamsForInstance(data.target, data.values ?? null, { revision: data.revision })
  }
}

const ensureListener = () => {
  if (listenerAttached) return
  window.addEventListener('message', handleMessage)
  listenerAttached = true
}

if (typeof window !== 'undefined') {
  ensureListener()
}

export type { ParamValue, ParamValues }
export const assetParamsBus = {
  ensureListener,
  registerInstance,
  unregisterInstance,
  setParamsForInstance,
  getParamsForInstance: (instanceId: string) => paramsByInstance.get(instanceId) ?? {},
}
